import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PhoneIcon from "@material-ui/icons/Phone";
import PhoneCallbackIcon from "@material-ui/icons/PhoneCallback";
import PhoneForwardedIcon from "@material-ui/icons/PhoneForwarded";
import PhoneMissedIcon from "@material-ui/icons/PhoneMissed";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, overflow: "hidden", height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: { "&:hover": { backgroundColor: "#ebf8ff" }, "&:nth-of-type(even)": { backgroundColor: "#fafafa" } },
}));

const PERIOD_OPTIONS = [
  { label: "Hoje", value: 0 },
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 15 dias", value: 15 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Personalizado", value: "custom" },
];

const TYPE_OPTIONS = [
  { label: "Todos os tipos", value: "" },
  { label: "Recebidas", value: "incoming" },
  { label: "Realizadas", value: "outgoing" },
];

const STATUS_CONFIG = {
  answered:  { label: "Atendida",   bg: "#f0fff4", color: "#276749",  pieColor: "#38a169" },
  missed:    { label: "Perdida",    bg: "#fff5f5", color: "#c53030",  pieColor: "#e53e3e" },
  rejected:  { label: "Rejeitada",  bg: "#fffaf0", color: "#c05621",  pieColor: "#ed8936" },
  busy:      { label: "Ocupado",    bg: "#fffff0", color: "#744210",  pieColor: "#d69e2e" },
  failed:    { label: "Falhou",     bg: "#faf5ff", color: "#6b46c1",  pieColor: "#9f7aea" },
};

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => { try { return format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR }); } catch { return d; } };
const fmtDateTime = (d) => { try { return format(new Date(d), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return "—"; } };
const fmtDuration = (secs) => {
  if (!secs) return "0s";
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export default function RelatorioChamadas() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 7)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sortBy, setSortBy] = useState("callStartedAt");
  const [sortDir, setSortDir] = useState("desc");

  const getDateRange = () => {
    if (period === "custom") return { start: customStart, end: customEnd };
    const days = period === 0 ? 1 : period;
    return { start: fmt(subDays(new Date(), days)), end: fmt(new Date()) };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = { dateStart: start, dateEnd: end, pageSize: 300 };
      if (typeFilter) params.type = typeFilter;
      const [listRes, summaryRes] = await Promise.all([
        api.get("/call-records", { params }),
        api.get("/call-records/summary", { params: { dateStart: start, dateEnd: end } }).catch(() => ({ data: null })),
      ]);
      setChamadas(Array.isArray(listRes.data) ? listRes.data : listRes.data?.records || listRes.data?.data || []);
      setSummary(summaryRes.data);
    } catch {
      setChamadas([]);
      setSummary(null);
    }
    setLoading(false);
  }, [period, customStart, customEnd, typeFilter]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period, typeFilter]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = chamadas
    .filter(c => !search || (c.contact?.name || c.fromNumber || c.toNumber || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortBy] ?? "", vb = b[sortBy] ?? "";
      if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const totalChamadas = chamadas.length;
  const atendidas = chamadas.filter(c => c.status === "answered").length;
  const perdidas = chamadas.filter(c => c.status === "missed").length;
  const entrantes = chamadas.filter(c => c.type === "incoming").length;
  const saintes = chamadas.filter(c => c.type === "outgoing").length;
  const totalDuration = chamadas.reduce((s, c) => s + (c.duration || 0), 0);
  const avgDuration = atendidas > 0 ? Math.round(totalDuration / atendidas) : 0;
  const taxaAtendimento = totalChamadas > 0 ? Math.round((atendidas / totalChamadas) * 100) : 0;

  const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, k) => {
    acc[k] = chamadas.filter(c => c.status === k).length;
    return acc;
  }, {});

  const pieData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label, value: v, color: STATUS_CONFIG[k]?.pieColor }));

  const barData = [
    { name: "Recebidas", value: entrantes, color: "#4299e1" },
    { name: "Realizadas", value: saintes, color: "#9f7aea" },
  ].filter(d => d.value > 0);

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "Hoje";
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((c, i) => {
      const sc = STATUS_CONFIG[c.status] || {};
      return `<tr>
        <td>${i + 1}</td>
        <td>${c.contact?.name || "—"}</td>
        <td>${c.fromNumber || c.toNumber || "—"}</td>
        <td>${c.type === "incoming" ? "Recebida" : "Realizada"}</td>
        <td><span style="padding:2px 10px;border-radius:12px;background:${sc.bg};color:${sc.color};font-weight:600">${sc.label || c.status}</span></td>
        <td>${fmtDuration(c.duration)}</td>
        <td>${c.callStartedAt ? fmtDateTime(c.callStartedAt) : "—"}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Chamadas</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#4299e1;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>📞 Relatório de Chamadas</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Contato</th><th>Número</th><th>Tipo</th><th>Status</th><th>Duração</th><th>Data/Hora</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">${totalChamadas} chamadas · Taxa de atendimento: ${taxaAtendimento}%</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-chamadas-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Chamadas</Typography>
          <Typography className={classes.subtitle}>Histórico e análise de chamadas recebidas e realizadas</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !filtered.length}
            style={{ backgroundColor: "#4299e1", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 130px" }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} label="Tipo">
            {TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#4299e1", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        <TextField placeholder="Buscar contato ou número..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ flex: "1 1 160px" }} />
        {loading && <CircularProgress size={20} />}
        {!loading && chamadas.length > 0 && (
          <Chip label={`${totalChamadas} chamadas · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#ebf8ff", color: "#2b6cb0", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        {[
          { label: "Total de Chamadas", value: totalChamadas, sub: `${entrantes} recebidas · ${saintes} realizadas`, bg: "linear-gradient(135deg,#ebf8ff,#bee3f8)", border: "#4299e1", textColor: "#2b6cb0", Icon: PhoneIcon },
          { label: "Atendidas", value: atendidas, sub: `${taxaAtendimento}% de atendimento`, bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38a169", textColor: "#276749", Icon: CheckCircleIcon },
          { label: "Perdidas", value: perdidas, sub: `${totalChamadas > 0 ? Math.round(perdidas / totalChamadas * 100) : 0}% do total`, bg: "linear-gradient(135deg,#fff5f5,#fed7d7)", border: "#e53e3e", textColor: "#c53030", Icon: PhoneMissedIcon },
          { label: "Recebidas", value: entrantes, sub: "Chamadas entrantes", bg: "linear-gradient(135deg,#faf5ff,#e9d8fd)", border: "#9f7aea", textColor: "#6b46c1", Icon: PhoneCallbackIcon },
          { label: "Realizadas", value: saintes, sub: "Chamadas saintes", bg: "linear-gradient(135deg,#fff3e5,#fbd38d)", border: "#ed8936", textColor: "#c05621", Icon: PhoneForwardedIcon },
          { label: "Duração Média", value: fmtDuration(avgDuration), sub: "Das chamadas atendidas", bg: "linear-gradient(135deg,#fffff0,#fefcbf)", border: "#d69e2e", textColor: "#744210", Icon: AccessTimeIcon },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card className={classes.metricCard} elevation={0}>
              <Box className={classes.metricInner} style={{ background: m.bg, borderTop: `3px solid ${m.border}` }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography className={classes.metricLabel} style={{ color: m.textColor }}>{m.label}</Typography>
                  <m.Icon style={{ color: m.border, fontSize: 22, opacity: 0.8 }} />
                </Box>
                <Typography className={classes.metricValue} style={{ color: m.textColor }}>
                  {loading ? "—" : m.value}
                </Typography>
                <Typography className={classes.metricSub} style={{ color: m.textColor }}>{m.sub}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Chamadas por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados no período</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [`${val} chamada${val !== 1 ? "s" : ""}`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Recebidas vs. Realizadas</Typography>
            {loading || barData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }} barSize={60}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [`${val} chamada${val !== 1 ? "s" : ""}`, "Quantidade"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Histórico de Chamadas ({filtered.length})</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 40 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>Contato</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Tipo</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "duration"} direction={sortBy === "duration" ? sortDir : "desc"}
                    onClick={() => handleSort("duration")} style={{ color: "#fff" }}>
                    Duração
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Atendente</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "callStartedAt"} direction={sortBy === "callStartedAt" ? sortDir : "desc"}
                    onClick={() => handleSort("callStartedAt")} style={{ color: "#fff" }}>
                    Data/Hora
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" style={{ padding: 40 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" style={{ padding: 40, color: "#a0aec0" }}>Nenhuma chamada encontrada no período selecionado</TableCell></TableRow>
              ) : filtered.map((c, i) => {
                const sc = STATUS_CONFIG[c.status] || { label: c.status, bg: "#f7fafc", color: "#718096" };
                const numero = c.type === "incoming" ? c.fromNumber : c.toNumber;
                return (
                  <TableRow key={c.id} className={classes.tableRow}>
                    <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{c.contact?.name || "Desconhecido"}</Typography>
                      <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{numero}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={c.type === "incoming" ? "Recebida" : "Realizada"}
                        size="small"
                        style={{
                          backgroundColor: c.type === "incoming" ? "#ebf8ff" : "#faf5ff",
                          color: c.type === "incoming" ? "#2b6cb0" : "#6b46c1",
                          fontWeight: 600, fontSize: 11
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sc.label} size="small"
                        style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, minWidth: 80 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontWeight: 600, fontSize: 13, color: c.duration > 60 ? "#276749" : "#718096" }}>
                        {fmtDuration(c.duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>{c.user?.name || "—"}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>
                        {c.callStartedAt ? fmtDateTime(c.callStartedAt) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
